import { Component, OnDestroy, OnInit } from '@angular/core'
import { FormControl } from '@angular/forms'
import { TranslateService } from '@ngx-translate/core'
import { Subscription } from 'rxjs'
import { Datum } from 'src/app/models/common/datum'
import {
  ServicePlanFieldError,
  ServicePlanStepComponent,
} from 'src/app/models/service-plan/service-plan-field-error'
import { ServicePlanStateService } from 'src/app/services/service-plan/service-plan-state.service'
import { ServicePlanStepAnnouncerService } from 'src/app/services/service-plan/service-plan-step-announcer.service'

const BULLET = '•'

// Step 10 — Service Continuity Planning (story e8-8, GSP/First Service Plan only —
// Planned Transition skips this step entirely via the existing visibility rules,
// untouched by this story). The 7 chips are local-only UI state (chipsControl is
// never written to the payload, which has no chips/checklist field) — decompiled
// from the mockup's bespoke per-chip pick() override, tapping an unselected chip
// appends its label as a new bullet line into notesControl, and only on that
// unselected -> selected transition; deselecting a chip clears its own pressed
// look but never touches notes text already written (see story Dev Notes "Exact
// chip-tap behaviour"). The field itself is optional (AC3) — isLiveValid is
// intentionally omitted, matching FamilyGoalsStepComponent's precedent.
@Component({
  selector: 'app-service-plan-continuity-planning-step',
  templateUrl: './continuity-planning-step.component.html',
  styleUrls: ['./continuity-planning-step.component.scss'],
  standalone: false,
})
export class ContinuityPlanningStepComponent
  implements OnInit, OnDestroy, ServicePlanStepComponent
{
  notesControl!: FormControl<string | null>
  chipsControl = new FormControl<string[]>([], { nonNullable: true })

  introText = ''
  chipsLabel = ''
  notesLabel = ''
  notesPlaceholder = ''
  reminderText = ''
  chipOptions: Datum[] = []

  // Chips always render unselected on (re-)entry (no payload field to re-derive a
  // chip's selected state from — see Dev Notes "Payload & state"), so this starts empty
  // every time the step mounts, not seeded from notesControl's existing content.
  private previousChips: string[] = []
  private readonly subscription = new Subscription()

  constructor(
    private state: ServicePlanStateService,
    private translate: TranslateService,
    private announcer: ServicePlanStepAnnouncerService,
  ) {}

  ngOnInit(): void {
    const t = (key: string) => this.translate.instant(key)

    this.introText = t('SERVICE_PLAN.STEP.CONTINUITY_PLANNING.INTRO')
    this.chipsLabel = t('SERVICE_PLAN.STEP.CONTINUITY_PLANNING.CHIPS_LABEL')
    this.notesLabel = t('SERVICE_PLAN.STEP.CONTINUITY_PLANNING.NOTES_LABEL')
    this.notesPlaceholder = t(
      'SERVICE_PLAN.STEP.CONTINUITY_PLANNING.NOTES_PLACEHOLDER',
    )
    this.reminderText = t('SERVICE_PLAN.STEP.CONTINUITY_PLANNING.REMINDER')

    this.chipOptions = [
      {
        label: t('SERVICE_PLAN.STEP.CONTINUITY_PLANNING.CHIP_BACKUP_DEVICES'),
        value: 'backup_devices',
      },
      {
        label: t(
          'SERVICE_PLAN.STEP.CONTINUITY_PLANNING.CHIP_EMERGENCY_LOANERS',
        ),
        value: 'emergency_loaners',
      },
      {
        label: t('SERVICE_PLAN.STEP.CONTINUITY_PLANNING.CHIP_BATTERIES'),
        value: 'batteries',
      },
      {
        label: t('SERVICE_PLAN.STEP.CONTINUITY_PLANNING.CHIP_DRYING'),
        value: 'drying',
      },
      {
        label: t('SERVICE_PLAN.STEP.CONTINUITY_PLANNING.CHIP_REPAIRS'),
        value: 'repairs',
      },
      {
        label: t('SERVICE_PLAN.STEP.CONTINUITY_PLANNING.CHIP_ASSISTIVE_TECH'),
        value: 'assistive_tech',
      },
      {
        label: t('SERVICE_PLAN.STEP.CONTINUITY_PLANNING.CHIP_TELEHEALTH'),
        value: 'telehealth',
      },
    ]

    this.notesControl = new FormControl<string | null>(
      this.state.getPayload().continuityPlanning.notes,
    )

    this.subscription.add(
      this.notesControl.valueChanges.subscribe((value) => {
        this.state.updatePayload({
          continuityPlanning: { notes: value },
        })
      }),
    )

    this.subscription.add(
      this.chipsControl.valueChanges.subscribe((next) => {
        const newlySelected = next.filter(
          (value) => !this.previousChips.includes(value),
        )
        for (const value of newlySelected) {
          const label = this.chipOptions.find(
            (option) => option.value === value,
          )?.label
          if (label === undefined) {
            continue
          }
          const current = this.notesControl.value
          this.notesControl.setValue(
            current ? `${current}\n${BULLET} ${label}` : `${BULLET} ${label}`,
          )
          this.announcer.announceMessage(
            this.translate.instant(
              'SERVICE_PLAN.STEP.CONTINUITY_PLANNING.CHIP_ADDED_ANNOUNCEMENT',
              { label },
            ),
          )
        }
        this.previousChips = next
      }),
    )
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe()
  }

  // ServicePlanStepComponent contract — the notes field is optional (AC3):
  // Continue is never blocked here, and isLiveValid is intentionally omitted
  // (shell default: not disabled), matching FamilyGoalsStepComponent's precedent.
  isValid(): boolean {
    return true
  }

  getValidationErrors(): ServicePlanFieldError[] {
    return []
  }
}
